import React, { useState, useEffect } from 'react'; // Added useEffect
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Coins, Search, PlusCircle, RefreshCcw, Loader2 } from 'lucide-react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'; // Added useMutation
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { resetUserCredits as resetUserCreditsAction } from '@/lib/actions/adminActions'; // Renamed to avoid conflict

// Interface for the combined data (profile + credits)
interface UserProfileWithCredits {
  id: string; // profile.id
  username: string | null;
  email: string | null;
  purchased_listings_credits: number;
  purchased_stocks_credits: number;
  free_listings_used: number; // from user_credits
  default_free_listings?: number; // from get_user_credits RPC
  free_listings_remaining?: number; // from get_user_credits RPC
}

const CoinsManagement = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserProfileWithCredits | null>(null); // Store full user object
  const [creditsAmount, setCreditsAmount] = useState<number>(0);
  const [creditsType, setCreditsType] = useState<'listings' | 'stocks'>('listings');
  // const [isAddingCredits, setIsAddingCredits] = useState(false); // Handled by mutation
  // const [isResettingUserCredits, setIsResettingUserCredits] = useState(false); // Handled by mutation


  // Fetch all profiles and their credits using get_user_credits RPC
  const { data: usersWithCredits = [], isLoading: isLoadingUsers } = useQuery({
    queryKey: ['allUserProfilesWithCredits'],
    queryFn: async (): Promise<UserProfileWithCredits[]> => {
      try {
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('id, username, email');

        if (profilesError) throw profilesError;
        if (!profilesData) return [];

        const usersData = await Promise.all(
          profilesData.map(async (profile) => {
            const { data: rpcData, error: rpcError } = await supabase.rpc(
              'get_user_credits',
              { p_user_id: profile.id }
            );

            if (rpcError) {
              console.error(`Error fetching credits for user ${profile.id}:`, rpcError);
              // Return profile data with default/zero credits on error
              return {
                ...profile,
                purchased_listings_credits: 0,
                purchased_stocks_credits: 0,
                free_listings_used: 0,
                default_free_listings: 0,
                free_listings_remaining: 0,
              };
            }
            
            const creditInfo = rpcData?.[0];
            return {
              ...profile,
              purchased_listings_credits: creditInfo?.purchased_listings_credits ?? 0,
              purchased_stocks_credits: creditInfo?.purchased_stocks_credits ?? 0,
              free_listings_used: creditInfo?.free_listings_used ?? 0,
              default_free_listings: creditInfo?.default_free_listings ?? 0,
              free_listings_remaining: creditInfo?.free_listings_remaining ?? 0,
            };
          })
        );
        return usersData as UserProfileWithCredits[];
      } catch (error) {
        console.error('Error fetching user profiles with credits:', error);
        toast({ title: "Error", description: "Could not load user credit data.", variant: "destructive" });
        return [];
      }
    }
  });
  
  const filteredUsers = usersWithCredits.filter(user => 
    user.username?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const addCreditsMutation = useMutation({
    mutationFn: async ({ userId, amount, type }: { userId: string, amount: number, type: 'listings' | 'stocks' }) => {
      const { data: existingData, error: fetchError } = await supabase
        .from('user_credits')
        .select('purchased_listings_credits, purchased_stocks_credits')
        .eq('user_id', userId)
        .maybeSingle();
      
      if (fetchError && fetchError.code !== 'PGRST116') throw fetchError;
      
      const updateField = type === 'listings' 
        ? 'purchased_listings_credits' 
        : 'purchased_stocks_credits';
      
      const currentValue = existingData ? (existingData[updateField] || 0) : 0;
      const newValue = currentValue + amount;
      
      const { error: upsertError } = await supabase
        .from('user_credits')
        .upsert({ 
          user_id: userId,
          [updateField]: newValue,
          updated_at: new Date().toISOString() 
        }, { onConflict: 'user_id' });

      if (upsertError) throw upsertError;
      return { userId, amount, type };
    },
    onSuccess: ({ amount, type }) => {
      toast({
        title: "Kreditet u shtuan me sukses",
        description: `${amount} kredite ${type === 'listings' ? 'njoftimesh' : 'produktesh'} u shtuan.`,
      });
      setSelectedUser(null);
      setCreditsAmount(0);
      queryClient.invalidateQueries({ queryKey: ['allUserProfilesWithCredits'] });
      queryClient.invalidateQueries({ queryKey: ['userCreditsRpcData', selectedUser?.id] }); // Also invalidate specific user
    },
    onError: (error: any) => {
      console.error('Error adding credits:', error);
      toast({
        title: "Gabim gjatë shtimit të krediteve",
        description: error.message || "Ndodhi një gabim i papritur",
        variant: "destructive",
      });
    }
  });


  const handleAddCredits = () => {
    if (!selectedUser || creditsAmount <= 0) return;
    addCreditsMutation.mutate({ userId: selectedUser.id, amount: creditsAmount, type: creditsType });
  };
  
  const resetCreditsMutation = useMutation({
    mutationFn: resetUserCreditsAction, // Use the imported admin action
    onSuccess: (data, variables) => { // variables includes userId
      if (data.success) {
        toast({ title: "Sukses", description: data.message || `Kreditet u rivendosën për përdoruesin.` });
        queryClient.invalidateQueries({ queryKey: ['allUserProfilesWithCredits'] });
        queryClient.invalidateQueries({ queryKey: ['userCreditsRpcData', variables] }); 
      } else {
        toast({ title: "Gabim", description: data.error || "Rivendosja e krediteve dështoi.", variant: "destructive" });
      }
    },
    onError: (error: any) => {
      toast({ title: "Gabim", description: error.message || "Ndodhi një gabim i papritur.", variant: "destructive" });
    }
  });

  const handleResetUserCredits = (userIdToReset: string) => {
    if (!userIdToReset) return;
    resetCreditsMutation.mutate(userIdToReset);
  };
  
  useEffect(() => {
    if (!selectedUser) {
      setCreditsAmount(0); // Reset amount when user selection is cleared
    }
  }, [selectedUser]);


  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Coins className="h-5 w-5" />
          Menaxhimi i Krediteve
        </CardTitle>
        <CardDescription>
          Shiko dhe menaxho kreditet e përdoruesve në platformë (listime falas, të blera).
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
            <Input
              type="search"
              placeholder="Kërko përdorues me emër ose email..."
              className="pl-8"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Përdoruesi</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Listime Falas (Përdorur/Total)</TableHead>
                  <TableHead>Kredite të Blera (Listime)</TableHead>
                  <TableHead>Kredite të Blera (Produkte)</TableHead>
                  <TableHead className="w-[200px]">Veprime</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoadingUsers ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-4"><Loader2 className="mx-auto h-6 w-6 animate-spin" />Duke ngarkuar...</TableCell></TableRow>
                ) : filteredUsers.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-4">Nuk u gjetën përdorues.</TableCell></TableRow>
                ) : (
                  filteredUsers.map((user) => (
                    <TableRow key={user.id} className={selectedUser?.id === user.id ? "bg-secondary" : ""}>
                      <TableCell>{user.username || "Pa emër përdoruesi"}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.free_listings_used} / {user.default_free_listings ?? 'N/A'}</TableCell>
                      <TableCell>{user.purchased_listings_credits}</TableCell>
                      <TableCell>{user.purchased_stocks_credits}</TableCell>
                      <TableCell className="space-x-2 flex items-center">
                        <Button 
                          variant={selectedUser?.id === user.id ? "default" : "outline"}
                          size="sm"
                          onClick={() => setSelectedUser(selectedUser?.id === user.id ? null : user)}
                        >
                          {selectedUser?.id === user.id ? 'Anulo' : 'Shto Kredite'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleResetUserCredits(user.id)}
                          disabled={resetCreditsMutation.isPending && resetCreditsMutation.variables === user.id}
                          title="Rivendos Kreditet Falas"
                        >
                          {(resetCreditsMutation.isPending && resetCreditsMutation.variables === user.id) ? (
                            <Loader2 className="animate-spin h-4 w-4" />
                          ) : (
                            <RefreshCcw className="h-4 w-4" />
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {selectedUser && (
            <div className="border rounded-md p-4 bg-gray-50 dark:bg-gray-800 space-y-4">
              <h3 className="font-medium dark:text-gray-200">
                {`Shto Kredite për ${selectedUser.username || selectedUser.email || 'Përdoruesin'}`}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium dark:text-gray-300">Lloji i Krediteve</label>
                  <div className="flex space-x-2">
                    <Button
                      variant={creditsType === 'listings' ? 'default' : 'outline'}
                      onClick={() => setCreditsType('listings')}
                      className="flex-1"
                    >
                      Njoftimet
                    </Button>
                    <Button
                      variant={creditsType === 'stocks' ? 'default' : 'outline'}
                      onClick={() => setCreditsType('stocks')}
                      className="flex-1"
                    >
                      Produktet (Stock)
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium dark:text-gray-300">Sasia</label>
                  <Input
                    type="number"
                    min={1}
                    value={creditsAmount || ''}
                    onChange={(e) => setCreditsAmount(parseInt(e.target.value) || 0)}
                    placeholder="Vendosni sasinë e krediteve"
                  />
                </div>
              </div>
              <Button
                onClick={handleAddCredits}
                disabled={addCreditsMutation.isPending || creditsAmount <= 0}
                className="w-full"
              >
                {addCreditsMutation.isPending ? (
                  <> <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Duke shtuar... </>
                ) : "Shto Kredite"}
                {!addCreditsMutation.isPending && <PlusCircle className="ml-2 h-4 w-4" />}
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default CoinsManagement;
